/* eslint-disable i18next/no-literal-string -- internal demo harness, not translated */
import {useState} from 'react'
import {endpoints} from '@/api/endpoints'
import {routes} from '@/lib/routes'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import type {EndpointContract} from '@/types/endpoint-contract'
import type {SearchAndFilters} from '@/hooks/use-server-pagination'

/**
 * Dev-only demo harness for UnifiedSearchBar. Not wired into production
 * navigation — used by Chrome DevTools screenshot verification and local
 * component review. Replaces Storybook (not installed in this repo).
 *
 * Visit `/demo/unified-search-bar` while running `./iam start` or
 * `npm run dev` to exercise every component state without a backend.
 *
 * Every filter in every card carries a `typeTable` that resolves against
 * `useAppConfig().typeValuesByName` so the panel and chips render localised
 * labels instead of raw enum codes. The `Active` BOOLEAN card is separate
 * from the `Status` multi-select card — one concept per card, no
 * same-entity redundancy.
 */

const PartyContract: EndpointContract = {
    sort: {NAME: 'name'},
    filter: {
        // Status as a multi-select — IdentityLifecycleState is a real project
        // type table (ACTIVE, SUSPENDED, TERMINATED) with EN + DE translations,
        // so the panel renders localised checkbox labels while the URL keeps
        // the raw codes. Multi-select ENUM is the §4.3 default unless the
        // field is genuinely binary.
        STATUS: {
            name: 'status',
            kind: 'csv',
            element: 'enum',
            values: ['ACTIVE', 'SUSPENDED', 'TERMINATED'],
            displayName: 'Status',
            typeTable: 'IdentityLifecycleState',
        },
        TYPE: {
            name: 'type',
            kind: 'csv',
            element: 'enum',
            // Real codes from the mock AppConfig's PartyType type table — so
            // the panel renders localized labels ('Person', 'Juristische Person'
            // in DE) via labelOf(typeValuesByName['PartyType'], code).
            values: ['PERSON', 'LEGAL_ENTITY', 'ORG_UNIT'],
            displayName: 'Type',
            typeTable: 'PartyType',
        },
        OWNER_ID: {
            name: 'ownerId',
            kind: 'uuid',
            displayName: 'Owner',
            // NOTE: real entity-picker combobox + chip-as-EntityLink are deferred
            // to Phase B.1 (Parties migration, issue 342.04). Until then this
            // renders as a plain UUID input (SEC-06 graceful fallback) so the
            // demo harness still covers the `uuid` FilterKind without needing
            // the backend endpoint wiring. See issue 342.02 Acceptance Criteria.
            referenceEndpoint: endpoints.parties.list(),
            detailRoute: routes.identityManagement.parties.detail(':id'),
        },
        NICKNAME: {
            name: 'nickname',
            kind: 'string',
            displayName: 'Nickname',
        },
        CREATED_SINCE: {
            name: 'createdSince',
            kind: 'date',
            displayName: 'Created since',
            dateRange: 'created',
            dateRangeBound: 'from',
        },
        CREATED_UNTIL: {
            name: 'createdUntil',
            kind: 'date',
            displayName: 'Created until',
            dateRange: 'created',
            dateRangeBound: 'to',
        },
        REPLACED_AT: {
            name: 'replacedAt',
            kind: 'datetime',
            displayName: 'Replaced at',
            nullable: true,
        },
        SECRET: {
            name: 'secret',
            kind: 'enum',
            values: ['yes', 'no'],
            displayName: 'Secret (admin)',
            adminOnly: true,
        },
    },
    search: {param: 'q', placeholder: 'Search demo...'},
}

/**
 * Separate contract for the BOOLEAN demo card — two genuinely binary
 * fields that would not make sense as multi-select enums (a profile is
 * either featured or not, archived or not). Keeping this on its own
 * contract avoids mixing same-entity concepts in one demo card.
 */
const FeatureContract: EndpointContract = {
    sort: {NAME: 'name'},
    filter: {
        FEATURED: {
            name: 'featured',
            kind: 'boolean',
            displayName: 'Featured',
            booleanLabels: {true: 'Featured', false: 'Not featured'},
        },
        ARCHIVED: {
            name: 'archived',
            kind: 'boolean',
            displayName: 'Archived',
            booleanLabels: {true: 'Archived', false: 'Live'},
        },
    },
    search: {param: 'q', placeholder: 'Search feature demo...'},
}

interface StateCardProps {
    title: string
    description: string
    filterKind?: string
    initial: SearchAndFilters
    contract?: EndpointContract
    testId: string
}

function StateCard({title, description, filterKind, initial, contract = PartyContract, testId}: StateCardProps) {
    const [value, setValue] = useState<SearchAndFilters>(initial)
    const onChange = (next: Partial<SearchAndFilters>) => {
        setValue(prev => ({
            q: next.q ?? prev.q,
            filters: next.filters ?? prev.filters,
            dynamicAttributes: next.dynamicAttributes ?? prev.dynamicAttributes,
        }))
    }
    return (
        <section className="rounded-lg border p-4 space-y-3" data-testid={testId}>
            <div>
                <div className="flex items-baseline justify-between gap-3">
                    <h2 className="text-lg font-semibold" data-testid="demo-card-title">{title}</h2>
                    {filterKind && (
                        <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
                            {filterKind}
                        </span>
                    )}
                </div>
                <p className="text-sm text-muted-foreground">{description}</p>
            </div>
            <UnifiedSearchBar
                contract={contract}
                value={value}
                onChange={onChange}
                attributeEntityType="party"
            />
            <pre className="rounded bg-muted p-2 text-xs overflow-x-auto">
                {JSON.stringify(value, null, 2)}
            </pre>
        </section>
    )
}

export function UnifiedSearchBarDemo() {
    return (
        <div className="mx-auto max-w-6xl space-y-6 p-6">
            <header>
                <h1 className="text-2xl font-bold" data-testid="demo-page-title">UnifiedSearchBar — component harness</h1>
                <p className="text-sm text-muted-foreground">
                    Demo states for Chrome DevTools verification. Each card is independent;
                    each renders the same component with a different initial state. Every
                    filter declares a `typeTable` against the mock AppConfig so the panel
                    and chips render localised labels.
                </p>
            </header>

            <StateCard
                title="Empty state"
                description="Bar with no active filters. Only placeholder, search icon, filter icon visible."
                initial={{q: '', filters: {}, dynamicAttributes: {}}}
                testId="demo-empty"
            />

            <StateCard
                title="Text-only state"
                filterKind="q (text)"
                description="User has submitted a free-text query — no filter chips, but q is bound."
                initial={{q: 'alex', filters: {}, dynamicAttributes: {}}}
                testId="demo-text-only"
            />

            <StateCard
                title="Status multi-select — checkbox list"
                filterKind="List<Enum>  →  checkbox"
                description="Status is the project's canonical multi-select filter. Controller signature: ?status=STATUS_CODE,STATUS_CODE (List<Enum>) — auto-inferred to a checkbox list. The panel renders localised labels via labelOf(typeValuesByName['IdentityLifecycleState'], code): 'Active' / 'Suspended' (EN) or 'Aktiv' / 'Gesperrt' (DE). Spec §4.3: default to List<Enum> for filter params unless the field is genuinely binary."
                initial={{q: '', filters: {STATUS: ['ACTIVE', 'SUSPENDED']}, dynamicAttributes: {}}}
                testId="demo-status-multi"
            />

            <StateCard
                title="Multi-select ENUM — PartyType"
                filterKind="List<Enum>  →  checkbox"
                description="Party type is a second multi-select dimension. Two values are active — chip reads 'Type: Person, Legal Entity' via labelOf(typeValuesByName['PartyType'], code)."
                initial={{q: '', filters: {TYPE: ['PERSON', 'LEGAL_ENTITY']}, dynamicAttributes: {}}}
                testId="demo-multi-enum"
            />

            <StateCard
                title="Three-plus chips (overflow wraps)"
                filterKind="mixed"
                description="Multiple filters + text query. At narrow viewport the bar wraps to a second line. Every chip value renders the localized label from the descriptor's typeTable — 'Status: Active, Suspended', 'Type: Person, Legal Entity' in EN; the DE run renders 'Aktiv, Gesperrt' and 'Person, Juristische Person' respectively."
                initial={{
                    q: 'alex',
                    filters: {
                        STATUS: ['ACTIVE', 'SUSPENDED'],
                        TYPE: ['PERSON', 'LEGAL_ENTITY'],
                        NICKNAME: 'ninja',
                    },
                    dynamicAttributes: {},
                }}
                testId="demo-many-chips"
            />

            <StateCard
                title="BOOLEAN filters — different entity (feature flags)"
                filterKind="Boolean  →  radio"
                description="Boolean is for genuinely binary fields — featured vs not, archived vs live. A different contract (FeatureContract) so the Party Status card isn't crowded with an overlapping ACTIVE/INACTIVE boolean. Per-field booleanLabels drive the chip rendering: chip reads 'Featured' / 'Archived', not 'Featured: true' / 'Archived: false'."
                initial={{q: '', filters: {FEATURED: 'true', ARCHIVED: 'false'}, dynamicAttributes: {}}}
                contract={FeatureContract}
                testId="demo-boolean"
            />

            <StateCard
                title="IS NULL — Not set"
                filterKind="DateTime + nullable"
                description="Replaced at has the __null__ sentinel — chip reads &quot;Not set&quot;."
                initial={{q: '', filters: {REPLACED_AT: '__null__'}, dynamicAttributes: {}}}
                testId="demo-null-sentinel"
            />

            <StateCard
                title="UUID filter — polymorphic-FK fallback"
                filterKind="UUID  →  raw-UUID input"
                description="After issue 525 every kind:'uuid' filter ships a `lookup` block that renders a typed Combobox. The raw-UUID input is the documented fallback for the only remaining unwired case — ErrorLog.sourceId (polymorphic FK whose target depends on sourceType; needs its own dispatch design)."
                initial={{q: '', filters: {OWNER_ID: '00000000-0000-0000-0000-000000000001'}, dynamicAttributes: {}}}
                testId="demo-uuid-pending"
            />

            <StateCard
                title="Dynamic attribute filter"
                filterKind="AppConfig attr.*"
                description="AppConfig-driven attribute filter (attr.costCenter). Dynamic attributes stay as plain-text chips — no entity-link treatment — even if the value happens to be a UUID."
                initial={{q: '', filters: {}, dynamicAttributes: {costCenter: 'CC-1234'}}}
                testId="demo-dynamic-attr"
            />

            <StateCard
                title="Date range applied"
                filterKind="Date dateRange group"
                description="From/to date pair → two URL params, one chip-pair visible. On desktop the panel renders a Popover-anchored calendar picker; on narrow (<480px) viewports it falls back to the browser's native date input (§5.8)."
                initial={{
                    q: '',
                    filters: {CREATED_SINCE: '2030-01-01', CREATED_UNTIL: '2030-12-31'},
                    dynamicAttributes: {},
                }}
                testId="demo-daterange"
            />
        </div>
    )
}

export default UnifiedSearchBarDemo
